import { useState } from "react";
import type { Task } from "@common/types/task";
import type { BoardColumnDef } from "@design-system/Board/Board.types";

interface ViewedColumn {
    taskId: string;
    /** The open task's column when this was set; null while filters hide it. */
    taskColumnId: string | null;
    columnId: string;
}

interface UseViewedTaskColumnArgs {
    openTaskId: string;
    columns: readonly BoardColumnDef[];
    tasksByColumnId: ReadonlyMap<string, readonly Task[]>;
}

const findTaskColumnId = ({
    openTaskId,
    columns,
    tasksByColumnId,
}: UseViewedTaskColumnArgs) =>
    columns.find((column) =>
        tasksByColumnId.get(column.id)?.some(({ id }) => id === openTaskId),
    )?.id ?? null;

/**
 * The column the open task's panel shows: the task's column, until the user
 * picks another with the switcher. It follows the task when the task opens
 * or moves to another column, and stays put while filters hide the task.
 */
export const useViewedTaskColumn = (args: UseViewedTaskColumnArgs) => {
    const { openTaskId, columns } = args;
    const taskColumnId = findTaskColumnId(args);
    const [stored, setStored] = useState<ViewedColumn | null>(null);

    const hasTaskMoved =
        taskColumnId !== null &&
        (stored?.taskId !== openTaskId || stored.taskColumnId !== taskColumnId);
    const viewed: ViewedColumn | null = hasTaskMoved
        ? { taskId: openTaskId, taskColumnId, columnId: taskColumnId }
        : stored;
    // Remembered so the panel keeps this column once filters hide the task.
    if (hasTaskMoved) {
        setStored(viewed);
    }

    const isViewedShown =
        viewed !== null &&
        columns.some((column) => column.id === viewed.columnId);
    const columnId = isViewedShown
        ? viewed.columnId
        : (taskColumnId ?? columns.at(0)?.id ?? null);

    const setColumnId = (nextColumnId: string) =>
        setStored({
            taskId: openTaskId,
            taskColumnId:
                viewed?.taskId === openTaskId ? viewed.taskColumnId : null,
            columnId: nextColumnId,
        });

    return { columnId, setColumnId };
};
